import * as React from 'react';

import { Separator, type SeparatorTone } from '@/hen-ui/atomics/Separator';
import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { GRID_MAX_CVA_COLUMNS } from '@/hen-ui/composites/Grid/grid.variants';

export interface StatColumnProps {
  /** The figure or short result shown in bold. */
  value: string;
  /** The words shown under the value. */
  label: string;
}

/**
 * One figure in a stat row: a bold value with a short label under it.
 */
export const StatColumn = React.forwardRef<HTMLElement, StatColumnProps>(
  ({ value, label }, ref) => {
    return (
      <Flex
        ref={ref}
        direction='column'
        align='center'
        gap='xxs'
        className='min-w-0 flex-1 px-sm py-sm md:py-0'
        data-hen-id={COMPONENT_IDS.StatColumn}
      >
        <Text variant='b4' weight='bold' align='center'>
          {value}
        </Text>
        <Text variant='b5' color='subtext' align='center'>
          {label}
        </Text>
      </Flex>
    );
  },
);

StatColumn.displayName = 'StatColumn';

const isFragment = (
  child: React.ReactNode,
): child is React.ReactElement<{ children?: React.ReactNode }> =>
  React.isValidElement(child) && child.type === React.Fragment;

const isStatColumnElement = (
  child: React.ReactNode,
): child is React.ReactElement<StatColumnProps> => {
  if (!React.isValidElement(child)) return false;
  if (child.type === StatColumn) return true;
  const type = child.type as { displayName?: string };
  return type.displayName === 'StatColumn';
};

/** Stat column children only. Fragments are opened so a grouped row still counts each column. */
const flattenColumns = (
  children: React.ReactNode,
): React.ReactElement<StatColumnProps>[] => {
  const flat: React.ReactElement<StatColumnProps>[] = [];

  React.Children.forEach(children, (child) => {
    if (isFragment(child)) {
      flat.push(...flattenColumns(child.props.children));
      return;
    }
    if (isStatColumnElement(child)) flat.push(child);
  });

  return flat;
};

export interface StatColumnsProps {
  /**
   * Stat column components rendered inside this row.
   * Only the first six are shown.
   */
  children?: React.ReactNode;
  /**
   * Show a line between columns. On by default.
   */
  columnDividers?: boolean;
  /**
   * How the lines read against the surface behind the row. Omit for the usual
   * divider. Use `stronger` on a gray card and `inverse` on a dark surface.
   */
  dividerTone?: SeparatorTone | '';
}

/**
 * Equal-width columns in one row from `md` up, stacked on mobile. Each column
 * is a value over a label. A line sits between columns (horizontal while
 * stacked, vertical in the row) unless `columnDividers` is turned off.
 */
export const StatColumns = React.forwardRef<HTMLElement, StatColumnsProps>(
  ({ children, columnDividers = true, dividerTone }, ref) => {
    const columns = flattenColumns(children).slice(0, GRID_MAX_CVA_COLUMNS);
    const row: React.ReactNode[] = [];

    columns.forEach((column, index) => {
      if (columnDividers && index > 0) {
        row.push(
          <Flex
            key={`stat-columns-divider-${index}`}
            direction='column'
            align='stretch'
            gap='none'
            className='self-stretch shrink-0'
          >
            <Separator tone={dividerTone} className='md:hidden' aria-hidden />
            <Separator
              orientation='vertical'
              tone={dividerTone}
              className='hidden md:block'
              aria-hidden
            />
          </Flex>,
        );
      }
      row.push(React.cloneElement(column, { key: `stat-column-${index}` }));
    });

    return (
      <Flex
        ref={ref}
        direction='column'
        direction-md='row'
        align='stretch'
        wrap='nowrap'
        gap='none'
        width='full'
        data-hen-id={COMPONENT_IDS.StatColumns}
      >
        {row}
      </Flex>
    );
  },
);

StatColumns.displayName = 'StatColumns';
